// Session lifecycle: keeps the JWT fresh while the user is active and
// surfaces an "are you still there?" event when they aren't. Backend
// signs 30-min tokens; this module refreshes them in the background
// before expiry, and emits `idle-warning` after 25 min of inactivity
// so the UI can confirm or terminate the session.

import store from '../store'
import router from '../router'
import api from './api'

const IDLE_THRESHOLD_MS = 25 * 60 * 1000   // show warning after 25 min idle
const REFRESH_BUFFER_MS = 5 * 60 * 1000    // refresh once we're within 5 min of expiry
const TICK_INTERVAL_MS = 15 * 1000         // check every 15s
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'wheel']

const listeners = new Set()
let lastActivityMs = Date.now()
let warningShown = false
let refreshInFlight = null
let tickInterval = null
let started = false

function emit(event, payload) {
  for (const l of listeners) {
    try { l(event, payload) } catch (e) { /* noop */ }
  }
}

function on(handler) {
  listeners.add(handler)
  return () => listeners.delete(handler)
}

// Decode the `exp` claim from a JWT without verifying — we only need
// to know when the server-issued token will become invalid so the
// client can refresh ahead of time.
function decodeExpMs(token) {
  if (!token) return null
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const payload = JSON.parse(decodeURIComponent(escape(atob(b64))))
    return payload.exp ? payload.exp * 1000 : null
  } catch {
    return null
  }
}

function handleActivity() {
  lastActivityMs = Date.now()
}

async function refresh() {
  if (refreshInFlight) return refreshInFlight
  refreshInFlight = (async () => {
    try {
      const res = await api.post('/auth/refresh', {}, { silent: true })
      const newToken = res?.data?.token
      if (newToken) {
        store.commit('auth/SET_TOKEN', newToken)
        return newToken
      }
      return null
    } catch {
      return null
    } finally {
      refreshInFlight = null
    }
  })()
  return refreshInFlight
}

async function continueSession() {
  warningShown = false
  handleActivity()
  const newToken = await refresh()
  if (!newToken) forceLogout('refresh-failed')
}

function forceLogout(reason) {
  stop()
  store.dispatch('auth/logout')
  if (router.currentRoute.value.path !== '/login') {
    router.push('/login')
  }
  emit('logout', reason)
}

function tick() {
  const token = store.getters['auth/token']
  if (!token) {
    stop()
    return
  }
  const now = Date.now()
  const idleFor = now - lastActivityMs
  const expMs = decodeExpMs(token)

  // If we can't read the expiry, fall back to idle-only behavior.
  if (expMs == null) {
    if (idleFor >= IDLE_THRESHOLD_MS && !warningShown) {
      warningShown = true
      emit('idle-warning')
    }
    return
  }

  const timeToExpiry = expMs - now

  // Token has already expired — bail; the next request would 401 and
  // the api interceptor would log out, but make it deterministic.
  if (timeToExpiry <= 0) {
    forceLogout('expired')
    return
  }

  // Idle warning fires once. Stays "shown" until the modal calls
  // continueSession() (clears it) or forceLogout() (kills the loop).
  if (idleFor >= IDLE_THRESHOLD_MS && !warningShown) {
    warningShown = true
    emit('idle-warning')
    return
  }

  // Only refresh when the user is active — otherwise let the warning
  // path handle it. This stops idle-forever sessions from rolling
  // tokens indefinitely.
  if (idleFor < IDLE_THRESHOLD_MS && timeToExpiry < REFRESH_BUFFER_MS) {
    refresh()
  }
}

function start() {
  if (started) return
  started = true
  lastActivityMs = Date.now()
  warningShown = false
  for (const e of ACTIVITY_EVENTS) {
    window.addEventListener(e, handleActivity, { passive: true, capture: true })
  }
  tickInterval = setInterval(tick, TICK_INTERVAL_MS)
}

function stop() {
  if (!started) return
  started = false
  for (const e of ACTIVITY_EVENTS) {
    window.removeEventListener(e, handleActivity, { capture: true })
  }
  if (tickInterval) {
    clearInterval(tickInterval)
    tickInterval = null
  }
  warningShown = false
}

export default {
  start,
  stop,
  on,
  refresh,
  continueSession,
  forceLogout
}
